import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import EdpiesLogo from '@/components/EdpiesLogo';
import { 
  Home, Brain, GraduationCap, Library, Pencil, 
  ChevronRight, Palette, Type, Layers, Sparkles, Check,
  BookOpen, Target, TrendingUp, Users, FileText, MessageCircle,
  Award, Zap, Trophy, Flame, Lightbulb
} from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Eds Microservices Brand Data
const edsMicroservices = [
  {
    id: 'smartpath',
    name: 'Eds SmartPath™',
    tagline: 'My Personal Learning Planner',
    description: 'AI-powered adaptive learning system that diagnoses strengths, creates personalized weekly plans, and tracks progress.',
    icon: Brain,
    gradient: 'from-emerald-500 to-teal-500',
    bgGradient: 'from-emerald-500/10 to-teal-500/10',
    borderColor: 'border-emerald-500/30',
    textColor: 'text-emerald-400',
    features: ['Diagnostic Assessment', 'Weekly Learning Plans', 'AI Explanations', 'Progress Tracking'],
    route: '/smartpath',
    status: 'Live'
  },
  {
    id: 'uniguide',
    name: 'Eds UniGuide™',
    tagline: 'From Ambition to Admission',
    description: 'AI-powered university counsellor for Year 13 students navigating the UCAS application journey.',
    icon: GraduationCap,
    gradient: 'from-indigo-500 via-purple-500 to-pink-500',
    bgGradient: 'from-indigo-500/10 via-purple-500/10 to-pink-500/10',
    borderColor: 'border-indigo-500/30',
    textColor: 'text-indigo-400',
    features: ['University Matching', 'Personal Statements', 'Mock Interviews', 'Predictive Analytics'],
    route: '/pathways',
    status: 'Live'
  },
  {
    id: 'library',
    name: 'Eds Library™',
    tagline: 'Read. Learn. Grow.',
    description: 'Interactive reading platform with AI-powered vocabulary tools, comprehension quizzes, and an intelligent tutor.',
    icon: Library,
    gradient: 'from-amber-500 to-orange-500',
    bgGradient: 'from-amber-500/10 to-orange-500/10',
    borderColor: 'border-amber-500/30',
    textColor: 'text-amber-400',
    features: ['Smart Reading', 'AI Tutor', 'Vocabulary Builder', 'Progress Tracking'],
    route: '/library',
    status: 'Live'
  },
  {
    id: 'whiteboard',
    name: 'Eds Whiteboard X™',
    tagline: 'Stretch Your Thinking',
    description: 'Daily challenge space with expert-curated problems, progressive hints, and AI-powered guidance.',
    icon: Pencil,
    gradient: 'from-violet-500 to-purple-500',
    bgGradient: 'from-violet-500/10 to-purple-500/10',
    borderColor: 'border-violet-500/30',
    textColor: 'text-violet-400',
    features: ['Daily Challenges', 'Tiered Difficulty', 'Smart Hints', 'Progress Tracking'],
    route: '/whiteboard',
    status: 'Beta'
  }
];

// Brand Colors
const brandColors = [
  { name: 'Primary Purple', hex: '#8B5CF6', variable: '--purple-500', usage: 'Primary actions, headers' },
  { name: 'Indigo', hex: '#6366F1', variable: '--indigo-500', usage: 'Secondary actions, links' },
  { name: 'Emerald', hex: '#10B981', variable: '--emerald-500', usage: 'Success states, SmartPath' },
  { name: 'Amber', hex: '#F59E0B', variable: '--amber-500', usage: 'Warnings, highlights, Library' },
  { name: 'Cyan', hex: '#06B6D4', variable: '--cyan-500', usage: 'Info states, Highlights' },
  { name: 'Pink', hex: '#EC4899', variable: '--pink-500', usage: 'Accents, UniGuide' },
  { name: 'Slate 950', hex: '#020617', variable: '--slate-950', usage: 'Background dark' },
  { name: 'Slate 800', hex: '#1E293B', variable: '--slate-800', usage: 'Card backgrounds' }
];

// Typography
const typography = [
  { name: 'Display', font: 'Space Grotesk', weight: '700', size: '48-72px', usage: 'Hero titles, major headings' },
  { name: 'Heading', font: 'Space Grotesk', weight: '600-700', size: '24-36px', usage: 'Section titles, card headers' },
  { name: 'Subheading', font: 'Inter', weight: '500-600', size: '18-20px', usage: 'Subtitles, feature names' },
  { name: 'Body', font: 'Inter', weight: '400', size: '14-16px', usage: 'Paragraphs, descriptions' },
  { name: 'Caption', font: 'Inter', weight: '400-500', size: '12-14px', usage: 'Labels, metadata, badges' }
];

const BrandGuidelines = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <motion.div 
              className="flex items-center gap-2 cursor-pointer"
              whileHover={{ scale: 1.05 }}
              onClick={() => navigate('/')}
            >
              <EdpiesLogo size="sm" />
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Edpies</span>
            </motion.div>
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-300 hover:text-white hover:bg-slate-800">
                <Home className="h-4 w-4 mr-2" /> Home
              </Button>
              <Button variant="ghost" onClick={() => navigate('/about')} className="text-slate-300 hover:text-white hover:bg-slate-800">
                About
              </Button>
              <Button className="bg-gradient-to-r from-purple-600 to-indigo-600" onClick={() => navigate('/auth')}>
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-indigo-900/20 to-pink-900/20"></div>
        <motion.div 
          className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        
        <div className="max-w-6xl mx-auto text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm">
              <Palette className="h-4 w-4" /> Brand Guidelines
            </span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-7xl font-bold text-white mb-6" 
            style={{ fontFamily: 'Space Grotesk' }}
          >
            The <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-indigo-400 bg-clip-text text-transparent">Eds</span> Ecosystem
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-slate-400 max-w-3xl mx-auto mb-8"
          >
            A unified family of AI-powered educational microservices, each designed to support a specific part of the student journey — from early learning to university admission.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-3"
          >
            {edsMicroservices.map((service, idx) => (
              <span 
                key={service.id}
                className={`px-3 py-1.5 rounded-full bg-gradient-to-r ${service.bgGradient} ${service.borderColor} border text-sm ${service.textColor} font-medium`}
              >
                {service.name}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Microservices Section */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              <Layers className="inline h-8 w-8 mr-3 text-purple-400" />
              Eds Microservices
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Each "Eds" service is a specialized tool with its own identity, united by consistent branding principles.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {edsMicroservices.map((service, idx) => (
              <motion.div key={service.id} variants={fadeInUp}>
                <Card className={`bg-slate-800/50 ${service.borderColor} border hover:border-opacity-60 transition-all`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* Left - Icon & Name */}
                      <div className="flex items-start gap-4 lg:w-1/3">
                        <div className={`p-4 rounded-xl bg-gradient-to-br ${service.gradient} shadow-lg`}>
                          <service.icon className="h-8 w-8 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-xl font-bold text-white">{service.name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              service.status === 'Live' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
                            }`}>
                              {service.status}
                            </span>
                          </div>
                          <p className={`${service.textColor} italic text-sm`}>"{service.tagline}"</p>
                        </div>
                      </div>

                      {/* Middle - Description */}
                      <div className="lg:w-1/3">
                        <p className="text-slate-400 text-sm leading-relaxed">{service.description}</p>
                      </div>

                      {/* Right - Features */}
                      <div className="lg:w-1/3">
                        <p className="text-slate-500 text-xs uppercase tracking-wider mb-2">Key Features</p>
                        <div className="flex flex-wrap gap-2">
                          {service.features.map((feature, fidx) => (
                            <span key={fidx} className={`text-xs px-2 py-1 rounded-full bg-slate-700/50 ${service.textColor}`}>
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Gradient Bar */}
                    <div className={`mt-4 h-1 rounded-full bg-gradient-to-r ${service.gradient} opacity-50`}></div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Naming Convention */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              <Type className="inline h-8 w-8 mr-3 text-indigo-400" />
              Naming Convention
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Our naming follows a consistent pattern for brand recognition and clarity.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-400" /> Correct Format
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <p className="text-green-400 font-mono text-lg">Eds [Name]™</p>
                  <p className="text-slate-400 text-sm mt-2">Example: <span className="text-white">Eds SmartPath™</span></p>
                </div>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-400 mt-0.5" />
                    <span>Always start with "Eds" (short for Edpies)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-400 mt-0.5" />
                    <span>Service name in PascalCase (SmartPath, UniGuide)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-400 mt-0.5" />
                    <span>Include ™ symbol for trademark recognition</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-green-400 mt-0.5" />
                    <span>Each service has a unique tagline</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-400" /> Tagline Guidelines
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {edsMicroservices.map(service => (
                  <div key={service.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-700/30">
                    <span className={`font-medium ${service.textColor}`}>{service.name}</span>
                    <span className="text-slate-400 text-sm italic">"{service.tagline}"</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Color Palette */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              <Palette className="inline h-8 w-8 mr-3 text-pink-400" />
              Color Palette
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Our color system creates visual hierarchy and brand recognition across all Eds services.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {brandColors.map((color, idx) => (
              <motion.div
                key={color.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="bg-slate-800/50 border-slate-700/50 overflow-hidden">
                  <div 
                    className="h-24 w-full"
                    style={{ backgroundColor: color.hex }}
                  ></div>
                  <CardContent className="p-4">
                    <p className="font-semibold text-white text-sm">{color.name}</p>
                    <p className="text-slate-500 text-xs font-mono">{color.hex}</p>
                    <p className="text-slate-400 text-xs mt-2">{color.usage}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Typography */}
      <section className="py-16 px-4 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              <Type className="inline h-8 w-8 mr-3 text-cyan-400" />
              Typography
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Consistent typography ensures readability and brand cohesion across all platforms.
            </p>
          </motion.div>

          <div className="space-y-4">
            {typography.map((type, idx) => (
              <motion.div
                key={type.name}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="bg-slate-800/50 border-slate-700/50">
                  <CardContent className="p-6 flex flex-col md:flex-row md:items-center gap-4">
                    <div className="md:w-1/4">
                      <p className="text-purple-400 font-semibold">{type.name}</p>
                      <p className="text-slate-500 text-sm">{type.font}</p>
                    </div>
                    <div className="md:w-1/4">
                      <p className="text-slate-400 text-sm">
                        <span className="text-slate-500">Weight:</span> {type.weight}
                      </p>
                      <p className="text-slate-400 text-sm">
                        <span className="text-slate-500">Size:</span> {type.size}
                      </p>
                    </div>
                    <div className="md:w-2/4">
                      <p 
                        className="text-white"
                        style={{ 
                          fontFamily: type.font,
                          fontWeight: type.weight.split('-')[0],
                          fontSize: type.name === 'Display' ? '28px' : type.name === 'Heading' ? '22px' : '16px'
                        }}
                      >
                        {type.name === 'Display' ? 'Eds SmartPath™' : 
                         type.name === 'Heading' ? 'My Personal Learning Planner' :
                         type.name === 'Subheading' ? 'AI-Powered Adaptive Learning' :
                         type.name === 'Body' ? 'Creating personalized learning experiences for every student.' :
                         'Label • Caption • Metadata'}
                      </p>
                      <p className="text-slate-500 text-xs mt-1">{type.usage}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex justify-center items-center gap-2 mb-4">
            <EdpiesLogo size="sm" />
            <span className="text-xl font-bold bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent">Edpies</span>
          </div>
          <p className="text-slate-500 text-sm mb-6">
            The Eds ecosystem — empowering students at every stage of their educational journey.
          </p>
          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate('/')} className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <Home className="h-4 w-4 mr-2" /> Back to Home
            </Button>
            <Button onClick={() => navigate('/auth')} className="bg-gradient-to-r from-purple-600 to-indigo-600">
              Get Started <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
          <p className="text-slate-600 text-xs mt-8">© 2025 Edpies. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BrandGuidelines;
